import { useEffect } from 'react'

import type { NotificationFormType, NotificationFormValues } from '@/notificationForms'

import { HotwordsField } from '../../../../HotwordsField'

import { useTexts } from '../../../useTexts'
import { useMessageSettings } from './hooks/useMessageSettings'

import { form } from '../../../formState'
import { createValidateTemplateCustom } from '../../../hooks/createValidateTemplateCustom'

interface Props {
  notification: NotificationFormType
}

export function Message(props: Props) {
  const { notification } = props

  const notificationWayFields = form.useFormField('notificationWay')
  const notificationWay = notificationWayFields.field.value

  const templateDefaultLimitsField = form.useFormField('templateDefaultLimits')
  const templateDefaultLimits = templateDefaultLimitsField.field.value

  const ttemplateCustomLimitsField = form.useFormField('templateCustomLimits')
  const templateCustomLimits = ttemplateCustomLimitsField.field.value

  const formApi = form.useFormApi()

  const {
    includeMessageUnsubscriptionInstructions,
    includeMessageTrackingLink,
    notificationLimits,
    notificationType,
  } = useMessageSettings(notification)
  const texts = useTexts()

  const label = notificationWay === 'PHONE' ? texts.smsMessageLabel : texts.emailMessageLabel

  useEffect(() => {
    const unsubscribe = formApi.subscribeToField('templateCustom', field => {
      // EMAIL BACKUP SUBJECT must be visible and required if EMAIL BACKUP is visible and enabled
      // ETA RANGE (windowOfCommitmentToleranceMins) must be visible if @@ETARange@@ is present in the message
      const isEtaRangeHotwordPresent = field.value.indexOf('@@ETARange@@') !== -1
      formApi.setFieldVisibleState('windowOfCommitmentToleranceMins', isEtaRangeHotwordPresent)
    })

    return () => {
      unsubscribe()
    }
  }, [formApi])

  form.useAddFieldValidation<'templateCustom'>('templateCustom', (field, _formApi) => {
    const currentFieldValues = _formApi.getFieldValues()
    const currentNotificationWay = currentFieldValues.notificationWay || 'EMAIL'

    const validateTemplateCustom = createValidateTemplateCustom(
      texts,
      field.value,
      currentNotificationWay,
      includeMessageTrackingLink,
      includeMessageUnsubscriptionInstructions,
      templateDefaultLimits,
      templateCustomLimits,
    )

    return (
      validateTemplateCustom.templateCustomRequired?.message ||
      validateTemplateCustom.templateCustomMaxLengthError?.message ||
      validateTemplateCustom.isSenderNameHotwordRequiredError?.message
    )
  })

  return (
    <HotwordsField<'templateCustom', NotificationFormValues>
      includeUnsubscriptionInstruction={includeMessageUnsubscriptionInstructions}
      includeTrackingLink={includeMessageTrackingLink}
      notificationLimits={notificationLimits}
      notificationType={notificationType}
      name="templateCustom"
      testId="templateCustom"
      label={label}
      notificationWay={notificationWay}
    />
  )
}
